import { InjectionToken } from '@angular/core';
import { resetStores } from '@datorama/akita';
import { IMetaInterfaces } from '@common/interfaces/metaInterfaces';
import { MetaInterfaceServiceProvider } from './project/state/metaInterfaces/metaInterfaces.provider.service';
import { MetaInterfacesQuery } from './project/state/metaInterfaces/metaInterfaces.query';
import { LocalStorageService } from './project/service/localstorage.service';
import { ProjectQuery } from './project/state/project/project.query';
import { WorkflowQuery } from './project/state/workflow/workflow.query';
import { IssueQuery } from './project/state/issue/issue.query';
import { ProjectScopeQuery } from './project/state/projectScope/projectScope.query';
import { Topics } from '@common/interfaces/topics';
import { persistedEntityCount, whenPersistedSnapshotReady } from '@persistence/akita-persist-storage';

export const STORAGE_INFO_KEY = new InjectionToken<string>('STORAGE_INFO_KEY');

/**
 * Registry degli store dinamici (le meta interfacce) da ricreare all'avvio: e' l'unica cosa che
 * IDB non sa dirci da solo. Le voci scritte dalle versioni precedenti contengono anche `hasValue`:
 * quel campo non viene piu' ne' letto ne' scritto (vedi isHydrationLost).
 */
export type StorageInfo = {
  name: string;
  isMeta: boolean;
  idKey: string;
};

// Tentativi di recupero dell'idratazione, contati per tab. sessionStorage e' la sede giusta:
// sopravvive al reload (che e' il nostro retry) e muore con la tab, quindi una sessione nuova non
// eredita i tentativi di quella precedente e due tab aperte non si sommano i contatori.
export const HYDRATION_ATTEMPTS_KEY = '_EP-INIT-HYDRATION-ATTEMPTS_';

// Oltre questa soglia si rinuncia a verificare l'idratazione: senza snapshot non c'e' un termine di
// confronto, e senza termine di confronto non si escala (mai un logout su un dubbio).
const SNAPSHOT_WAIT_TIMEOUT_MS = 5000;

const readHydrationAttempts = (): number => {
  try {
    const attempts = parseInt(sessionStorage.getItem(HYDRATION_ATTEMPTS_KEY), 10);
    return Number.isNaN(attempts) ? 0 : attempts;
  } catch (e) {
    // sessionStorage inaccessibile (modalita' privata, storage disabilitato): senza contatore
    // restiamo sullo stadio piu' conservativo, cioe' il reload.
    return 0;
  }
};

const writeHydrationAttempts = (attempts: number) => {
  try {
    sessionStorage.setItem(HYDRATION_ATTEMPTS_KEY, String(attempts));
  } catch (e) { }
};

const clearHydrationAttempts = () => {
  try {
    sessionStorage.removeItem(HYDRATION_ATTEMPTS_KEY);
  } catch (e) { }
};

/**
 * Uno store risulta non idratato se IDB conteneva entita' per lui ma lo store e' vuoto.
 *
 * Il confronto e' con lo snapshot di IDB letto all'avvio, non con un flag su localStorage: erano due
 * storage indipendenti e ogni loro divergenza (eviction di IDB, resetStores che riscrive stato
 * vuoto, entita' cancellate tutte, upsert di un array vuoto) diventava un logout immotivato, perche'
 * il flag continuava a promettere dati che non esistevano piu'.
 */
const isHydrationLost = (storeName: string, query: { getValue(): { ids?: any[] } }): boolean => {
  const persisted = persistedEntityCount(storeName);
  if (persisted === 0) {
    return false;
  }
  const current = query.getValue()?.ids?.length || 0;
  if (current > 0) {
    return false;
  }
  console.error(`[APP INIT FACTORY] store ${storeName}: IDB conteneva ${persisted} entita' ma lo store e' vuoto`);
  return true;
};

/**
 * Escalation in 3 stadi. L'idratazione, dopo il gate di main.ts, avviene dentro il costruttore dello
 * store: se e' andata persa non c'e' niente da attendere, si puo' solo ritentare da zero. Quindi si
 * sale di stadio a ogni fallimento e il logout - operazione pesante per l'utente - resta l'ultima
 * risorsa.
 *
 * Stadio 1: reload puro. Nessun reset, la sessione resta valida: e' solo una seconda possibilita'
 *           di idratarsi.
 * Stadio 2: cache locale scartata (compresi i cursori di lastUpdate, cosi' il realtime rifa' un
 *           resync completo invece dei soli delta) ma sessione preservata: si riparte da loggati
 *           riscaricando i dati dal backend.
 * Stadio 3: reset completo, che azzera anche lo store auth e quindi disconnette. Backstop.
 *
 * Negli stadi 2 e 3 la chiave di storage-info va rimossa insieme al reset: resetStores riscrive
 * stato vuoto su IDB, quindi tenere il registry vecchio significherebbe ricreare all'avvio
 * successivo store dinamici di cui non esiste piu' il contenuto.
 */
const recoverFromFailedHydration = (storeName: string, storage: LocalStorageService, storageKey: string) => {
  const attempts = readHydrationAttempts();
  writeHydrationAttempts(attempts + 1);

  if (attempts === 0) {
    console.warn(`[APP INIT FACTORY] store ${storeName} non idratato - stadio 1: reload, sessione preservata`);
    window.location.reload();
    return;
  }

  if (attempts === 1) {
    console.warn(`[APP INIT FACTORY] store ${storeName} non idratato al 2° tentativo - stadio 2: cache locale scartata, sessione preservata`);
    storage.remove(storageKey);
    resetStores({ exclude: ['auth', 'ui'] });
    window.location.reload();
    return;
  }

  console.error(`[APP INIT FACTORY] store ${storeName} non idratato al 3° tentativo - stadio 3: reset completo, logout forzato`);
  clearHydrationAttempts();
  storage.remove(storageKey);
  resetStores();
  window.location.reload();
};

/**
 * Auto-riparazione del registry degli store dinamici: registra lo store di ogni meta interfaccia
 * viva presente nel record store (idratato da IDB) ma assente dal provider.
 *
 * Il registry (StorageInfo, localStorage) e' una superficie di persistenza separata sia dal record
 * store che dal suo cursore di sync (entrambi su IDB): una voce persa (scrittura fenced da
 * canRuntimePersist, localStorage ripulito) non viene mai piu' ricostruita dal realtime, perche'
 * con il cursore valido la first sync incrementale non riconsegna i record invariati e il side
 * effect di registrazione non scatta. Senza questa riconciliazione lo store resta mancante per
 * tutta la sessione e ogni consumer dell'interfaccia esplode (`getService(...)` undefined).
 */
export const reconcileMetaInterfaceStores = (
  records: Pick<IMetaInterfaces, 'tableName' | 'deletedAt' | 'archivedAt'>[],
  provider: Pick<MetaInterfaceServiceProvider, 'hasStore' | 'addStore'>,
): void => {
  for (const record of records) {
    if (!record?.tableName || record.deletedAt || record.archivedAt) {
      continue;
    }
    if (provider.hasStore(record.tableName)) {
      continue;
    }
    console.warn(`[APP INIT FACTORY] store meta ${record.tableName} presente nel record store ma assente dal registry: lo ricreo`);
    provider.addStore({ name: record.tableName, idKey: 'id' });
  }
};

export const APP_INIT_FACTORY = (
  storageKey: string,
  interfaceProviderService: MetaInterfaceServiceProvider,
  storage: LocalStorageService,
  projectQuery: ProjectQuery,
  workflowQuery: WorkflowQuery,
  issueQuery: IssueQuery,
  projectScopeQuery: ProjectScopeQuery,
  metaInterfacesQuery: MetaInterfacesQuery,
) => async () => {
  console.log('[APP INIT FACTORY]');
  let stores = storage.get<StorageInfo[]>(storageKey);
  if (!stores) {
    // Registry assente (primo avvio, o localStorage ripulito con IDB intatto): si inizializza la
    // chiave ma NON si esce, cosi' la riconciliazione a valle puo' ricostruire gli store dinamici
    // dai record idratati da IDB.
    console.log('[APP INIT FACTORY] nessun registry: inizializzo la chiave');
    storage.set(storageKey, []);
    clearHydrationAttempts();
    stores = [];
  }

  // 1. Gli store dinamici delle meta interfacce vanno ricreati prima che arrivi il primo messaggio
  //    realtime: una batch activity su uno store inesistente esplode.
  const metaStores = stores.filter((store) => store.isMeta);
  if (metaStores.length) {
    console.log(`[APP INIT FACTORY] ricreo ${metaStores.length} store di meta interfacce`);
    interfaceProviderService.loadStores(metaStores.map(({ name, idKey }) => ({ name, idKey })));
  }

  // 2. Gli store core sono gia' idratati: il gate di main.ts garantisce che setInitial giri dentro
  //    il loro costruttore. Quelli meta sono stati creati un attimo fa e la loro idratazione passa
  //    per qualche microtask, che un singolo giro di event loop esaurisce. Nessun polling: dopo il
  //    gate, se uno store e' ancora vuoto, attendere non cambia la risposta.
  const snapshot = await Promise.race([
    whenPersistedSnapshotReady().then(() => 'ready' as const),
    delay(SNAPSHOT_WAIT_TIMEOUT_MS).then(() => 'timeout' as const),
  ]);
  if (snapshot === 'timeout') {
    console.error(`[APP INIT FACTORY] snapshot IDB non disponibile entro ${SNAPSHOT_WAIT_TIMEOUT_MS}ms: verifica saltata`);
    return;
  }
  await delay(0);

  // 3. Verifica puntuale, store per store.
  const toVerify: { name: string; query: { getValue(): { ids?: any[] } } }[] = [
    { name: Topics.PROJECTS, query: projectQuery },
    { name: Topics.WORKFLOWS, query: workflowQuery },
    { name: Topics.ISSUES, query: issueQuery },
    { name: Topics.PROJECT_SCOPES, query: projectScopeQuery },
    ...metaStores.map(({ name }) => ({ name, query: interfaceProviderService.getQuery(name) })),
  ];

  for (const { name, query } of toVerify) {
    if (!query) {
      continue;
    }
    if (isHydrationLost(name, query)) {
      // Il recupero comporta sempre un reload: si esce subito senza verificare il resto.
      recoverFromFailedHydration(name, storage, storageKey);
      return;
    }
  }

  // 4. Registry e record store possono divergere (vedi reconcileMetaInterfaceStores): i record
  //    delle meta interfacce, gia' idratati dopo il gate dello snapshot, sono la fonte di verita'.
  reconcileMetaInterfaceStores(metaInterfacesQuery.getAll(), interfaceProviderService);

  console.log('[APP INIT FACTORY] idratazione verificata');
  // Boot completato con tutti gli store idratati: l'eventuale escalation in corso e' chiusa.
  clearHydrationAttempts();
};

function delay(ms: number) {
  return new Promise((res) => {
    setTimeout(res, ms);
  });
}
